import { Injectable } from '@angular/core';
import { OfflineDataService } from './offline-data.service';
import { SendLogsService } from './send-logs.service';
import { SparkLightLoadService } from './spark-light-load.service';

type NativeBridgeResult = {
  ok: boolean;
  reason?: string;
  restored?: number;
  skipped?: number;
  errors?: string[];
};

/**
 * Contract used by Opto App iOS WKWebView to export/restore unsynced
 * IndexedDB data and to trigger Send logs when the Spark UI is stuck.
 */
@Injectable({ providedIn: 'root' })
export class SparkNativeBridgeService {
  constructor(
    private offlineDataService: OfflineDataService,
    private sendLogsService: SendLogsService,
    private lightLoad: SparkLightLoadService
  ) {
    this.install();
  }

  private install(): void {
    const win = window as any;
    win.__sparkNative = {
      exportBackup: () => this.exportBackup(),
      importBackup: (json: string) => this.importBackup(json),
      sendLogs: (context: string) => this.sendLogs(context),
      applyLightLoad: () => this.setLightLoad(true),
      setLightLoad: (enabled: boolean) => this.setLightLoad(!!enabled),
    };

    this.postToNative({ type: 'ready' });
    this.requestPersistentStorage();
  }

  private exportBackup(): string {
    try {
      return this.offlineDataService.getNativeBackupJson();
    } catch (e) {
      return JSON.stringify({
        version: 2,
        ok: false,
        mapReady: false,
        backedUpAt: new Date().toISOString(),
        patients: [],
        error: String(e),
      });
    }
  }

  private async importBackup(json: string): Promise<string> {
    try {
      const result = await this.offlineDataService.importNativeBackup(json);
      const payload: NativeBridgeResult = {
        ok: result.ok,
        restored: result.restored,
        skipped: result.skipped,
        errors: result.errors,
      };
      return JSON.stringify(payload);
    } catch (e) {
      return JSON.stringify({
        ok: false,
        reason: String(e),
        restored: 0,
        skipped: 0,
        errors: [String(e)],
      });
    }
  }

  private setLightLoad(enabled: boolean): string {
    try {
      this.lightLoad.setEnabled(enabled);
      if (enabled) {
        this.offlineDataService.applyLightLoadInPlace();
      } else {
        this.lightLoad.markForceFullRefresh();
      }
      this.postToNative({ type: 'lightLoad', enabled });
      return JSON.stringify({ ok: true, enabled });
    } catch (e) {
      return JSON.stringify({ ok: false, enabled, reason: String(e) });
    }
  }

  private sendLogs(context: string): Promise<string> {
    return this.sendLogsService
      .sendLogsAsync(context || 'Native Opto App send logs', {
        showAlert: false,
      })
      .then((result) => JSON.stringify(result));
  }

  private requestPersistentStorage(): void {
    try {
      const storage = (navigator as any).storage;
      if (storage && typeof storage.persist === 'function') {
        storage.persist().then((persisted: boolean) => {
          this.postToNative({ type: 'persist', persisted: !!persisted });
        });
      }
    } catch {
      // WKWebView may ignore persist(); native already treats this as best-effort.
    }
  }

  private postToNative(message: Record<string, unknown>): void {
    try {
      const handler = (window as any).webkit?.messageHandlers?.sparkDiagnostics;
      handler?.postMessage(message);
    } catch {
      // Running in a regular browser; native bridge is absent.
    }
  }
}
